
import React, { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import PlanFeatureIcon from './PlanFeatureIcon';

interface Feature {
  name: string;
  included: boolean;
}

interface PlanFeaturesProps {
  planId: string;
  keyFeatures: Feature[];
  otherFeatures: Feature[];
  openCollapsibles: Record<string, boolean>;
  toggleCollapsible: (planId: string) => void;
}

const PlanFeatures: React.FC<PlanFeaturesProps> = ({
  planId,
  keyFeatures,
  otherFeatures,
  openCollapsibles,
  toggleCollapsible
}) => {
  return (
    <>
      <div className="mt-3 space-y-2">
        {keyFeatures
          .filter(f => 
            !f.name.toLowerCase().includes('referral') || 
            (!f.name.toLowerCase().includes('referrals') && !f.name.toLowerCase().includes('max')))
          .map((feature, i) => (
            <div key={i} className="flex items-start">
              {feature.included ? (
                <>
                  <PlanFeatureIcon featureName={feature.name} />
                  <span className="text-sm line-clamp-1">
                    {feature.name}
                  </span>
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2 shrink-0 text-gray-300" />
                  <span className="text-sm text-gray-400 line-through line-clamp-1">
                    {feature.name}
                  </span>
                </>
              )}
            </div>
          ))}
      </div>
      
      {(keyFeatures.length > 0 || otherFeatures.length > 0) && (
        <Collapsible
          open={openCollapsibles[planId]}
          onOpenChange={() => toggleCollapsible(planId)}
          className="mt-3"
        >
          <CollapsibleTrigger asChild>
            <button 
              className="flex items-center text-xs text-blue-500 hover:text-blue-600"
              onClick={(e) => {
                e.preventDefault(); // Prevent radio selection when clicking this button
                toggleCollapsible(planId);
              }}
            >
              {openCollapsibles[planId] ? 'Show less' : 'Show all features'}
              <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${openCollapsibles[planId] ? 'rotate-180' : ''}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-2">
            {/* Show all features in the expanded view */}
            {[...keyFeatures, ...otherFeatures].map((feature, i) => (
              <div key={i} className="flex items-start">
                <div className={cn("h-4 w-4 mr-2 shrink-0", feature.included ? "text-green-500" : "text-gray-300")}>
                  {feature.included ? <Check className="h-4 w-4" /> : <div className="h-4 w-4 rounded-full border border-gray-300" />}
                </div>
                <span className={cn("text-sm line-clamp-2", feature.included ? "" : "text-gray-400")}>
                  {feature.name}
                </span>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </>
  );
};

export default PlanFeatures;
